import { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Alert,
  Button,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  TrendingUp as TrendIcon,
  People as TeamIcon,
  Warning as WarningIcon,
  CheckCircle as CompleteIcon,
  Schedule as InProgressIcon,
  Error as BlockedIcon,
  GetApp as ExportIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { mockProjects, mockInstallers, mockSchedule, historicalProjects, monthlyMetrics } from './mockData';

// Helper function to calculate project health based on budget variance and timeline
const getProjectHealth = (project) => {
  const budgetVariance = ((project.actualHours - project.budgetHours) / project.budgetHours) * 100;
  const now = new Date();
  const endDate = new Date(project.endDate);
  const isOverdue = now > endDate && project.status !== 'Completed';

  if (isOverdue || budgetVariance > 15) return 'critical';
  if (budgetVariance > 5 || budgetVariance < -10) return 'warning';
  return 'good';
};

// Helper function to get health color
const getHealthColor = (health) => {
  switch (health) {
    case 'critical': return 'error';
    case 'warning': return 'warning';
    case 'good': return 'success';
    default: return 'info';
  }
};

// Weekly Project Status Report Component
const WeeklyProjectStatus = () => {
  const activeProjects = mockProjects.filter(p => p.status === 'Active');
  const projectsAtRisk = activeProjects.filter(p => getProjectHealth(p) !== 'good');
  const totalBudgetHours = mockProjects.reduce((sum, p) => sum + p.budgetHours, 0);
  const totalActualHours = mockProjects.reduce((sum, p) => sum + p.actualHours, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Active Projects
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {activeProjects.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                out of {mockProjects.length} total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Projects At Risk
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {projectsAtRisk.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                need attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Hours
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {totalActualHours.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of {totalBudgetHours.toLocaleString()} budgeted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Budget Efficiency
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: totalActualHours <= totalBudgetHours ? 'success.main' : 'error.main' }}>
                {Math.round((totalActualHours / totalBudgetHours) * 100)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of total budget used
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Projects Status */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Active Projects Status
      </Typography>

      <Grid container spacing={2}>
        {activeProjects.map((project) => {
          const health = getProjectHealth(project);
          const budgetVariance = ((project.actualHours - project.budgetHours) / project.budgetHours) * 100;

          return (
            <Grid item xs={12} key={project.id}>
              <Card sx={{ mb: 1 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {project.jobNumber}
                        </Typography>
                        <Chip
                          label={project.pm}
                          size="small"
                          sx={{ bgcolor: project.pmColor, color: 'white' }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {project.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.client}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" gutterBottom>
                        Progress: {project.progress}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={project.progress}
                        color={getHealthColor(health)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Due: {new Date(project.endDate).toLocaleDateString()}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        Hours: {project.actualHours}/{project.budgetHours}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={budgetVariance > 5 ? 'error.main' : budgetVariance > 0 ? 'warning.main' : 'success.main'}
                      >
                        {budgetVariance > 0 ? '+' : ''}{budgetVariance.toFixed(1)}% vs budget
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Chip
                        label={health === 'good' ? 'On Track' : health === 'warning' ? 'At Risk' : 'Critical'}
                        color={getHealthColor(health)}
                        size="small"
                        icon={
                          health === 'good' ? <CompleteIcon /> :
                          health === 'warning' ? <WarningIcon /> :
                          <BlockedIcon />
                        }
                      />
                    </Grid>
                  </Grid>

                  {project.lastNote && (
                    <Alert severity={health === 'critical' ? 'error' : 'info'} sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Latest Update:</strong> {project.lastNote}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Historical Performance Trends */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
        6-Month Performance Trends
      </Typography>

      <Grid container spacing={3}>
        {/* Monthly Revenue Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Revenue & Projects
              </Typography>
              <Box sx={{ height: 200 }}>
                {monthlyMetrics.map((month, index) => (
                  <Box key={month.month} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2">{month.month}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        ${month.revenue.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(month.revenue / 12000) * 100} // Normalize to max revenue
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {month.projectsCompleted} projects
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Metrics
              </Typography>
              <Box sx={{ height: 200 }}>
                {monthlyMetrics.map((month, index) => (
                  <Box key={month.month} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>{month.month}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Efficiency
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 'bold',
                          color: month.averageEfficiency <= 1.0 ? 'success.main' : 'warning.main'
                        }}>
                          {Math.round(month.averageEfficiency * 100)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          On-Time
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 'bold',
                          color: month.onTimeCompletion >= 80 ? 'success.main' : 'warning.main'
                        }}>
                          {month.onTimeCompletion}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Completed Projects */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recently Completed Projects (September 2024)
              </Typography>
              <Grid container spacing={2}>
                {historicalProjects.filter(p => p.monthYear === 'September 2024').map(project => (
                  <Grid item xs={12} sm={6} key={project.id}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {project.jobNumber}
                        </Typography>
                        <Chip
                          label={project.pm}
                          size="small"
                          sx={{ bgcolor: project.pmColor, color: 'white' }}
                        />
                        <Chip
                          label={project.efficiency <= 1.0 ? 'Under Budget' : 'Over Budget'}
                          size="small"
                          color={project.efficiency <= 1.0 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {project.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.actualHours}h / {project.budgetHours}h • Completed {project.completedDate}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Budget vs Actual Dashboard Component
const BudgetActualDashboard = () => {
  // Calculate budget performance, excluding large multi-year projects from total variance
  const standardProjects = mockProjects.filter(p => !p.isLargeProject);
  const largeProjects = mockProjects.filter(p => p.isLargeProject);

  const overBudgetProjects = standardProjects.filter(p => p.actualHours > p.budgetHours);
  const underBudgetProjects = standardProjects.filter(p => p.actualHours < p.budgetHours * 0.9);

  // For large projects, calculate variance based on completed milestones only
  const largeProjectVariance = largeProjects.reduce((sum, p) => {
    if (!p.milestones) return sum;
    const completedMilestones = p.milestones.filter(m => m.status === 'Completed');
    const completedVariance = completedMilestones.reduce((mSum, m) => mSum + (m.actualHours - m.budgetHours), 0);
    return sum + completedVariance;
  }, 0);

  const standardProjectVariance = standardProjects.reduce((sum, p) => sum + (p.actualHours - p.budgetHours), 0);
  const totalVariance = standardProjectVariance + largeProjectVariance;

  return (
    <Box sx={{ p: 3 }}>
      {/* Budget Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', bgcolor: totalVariance > 0 ? 'error.50' : 'success.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Budget Variance
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: totalVariance > 0 ? 'error.main' : 'success.main' }}>
                {totalVariance > 0 ? '+' : ''}{totalVariance} hrs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalVariance > 0 ? 'Over' : 'Under'} budget across all projects
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Projects Over Budget
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {overBudgetProjects.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Require immediate attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Efficient Projects
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {underBudgetProjects.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed under 90% of budget
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Projects by Budget Performance */}
      <Typography variant="h6" gutterBottom>
        Project Budget Analysis
      </Typography>

      <Grid container spacing={2}>
        {mockProjects.map((project) => {
          // Handle large projects with milestones differently
          if (project.isLargeProject && project.milestones) {
            const completedMilestones = project.milestones.filter(m => m.status === 'Completed');
            const activeMilestone = project.milestones.find(m => m.status === 'Active');
            const completedVariance = completedMilestones.reduce((sum, m) => sum + (m.actualHours - m.budgetHours), 0);
            const totalCompletedBudget = completedMilestones.reduce((sum, m) => sum + m.budgetHours, 0);

            return (
              <Grid item xs={12} key={project.id}>
                <Card>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {project.jobNumber}
                          </Typography>
                          <Chip
                            label={project.pm}
                            size="small"
                            sx={{ bgcolor: project.pmColor, color: 'white' }}
                          />
                          <Chip
                            label="Multi-Phase"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {project.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Current Phase: {activeMilestone?.name || 'Planning'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Typography variant="body2">
                          Total Budget: {project.budgetHours} hrs
                        </Typography>
                        <Typography variant="body2">
                          Completed Phases: {completedMilestones.length} of {project.milestones.length}
                        </Typography>
                        <Typography variant="body2" color="primary.main">
                          Phase Performance: {completedVariance >= 0 ? '+' : ''}{completedVariance}h
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Progress: {project.progress}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={project.progress}
                          sx={{ mt: 1, height: 8, borderRadius: 4 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <Chip
                          label={completedVariance === 0 ? 'On Target' :
                                 completedVariance > 0 ? 'Over Budget' : 'Under Budget'}
                          color={completedVariance === 0 ? 'success' :
                                 completedVariance > 0 ? 'warning' : 'success'}
                          size="small"
                        />
                      </Grid>
                    </Grid>

                    {/* Milestone breakdown for large projects */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Phase Breakdown:
                      </Typography>
                      <Grid container spacing={1}>
                        {project.milestones.map(milestone => (
                          <Grid item xs={12} sm={4} key={milestone.id}>
                            <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                              <Typography variant="caption" color="text.secondary">
                                {milestone.name}
                              </Typography>
                              <Typography variant="body2" sx={{
                                fontWeight: 'bold',
                                color: milestone.status === 'Completed' ? 'success.main' :
                                       milestone.status === 'Active' ? 'primary.main' : 'text.secondary'
                              }}>
                                {milestone.actualHours}h / {milestone.budgetHours}h
                              </Typography>
                              <Chip
                                label={milestone.status}
                                size="small"
                                color={milestone.status === 'Completed' ? 'success' :
                                       milestone.status === 'Active' ? 'primary' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          // Standard project handling
          const variance = project.actualHours - project.budgetHours;
          const variancePercentage = (variance / project.budgetHours) * 100;
          const isOverBudget = variance > 0;

          return (
            <Grid item xs={12} key={project.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {project.jobNumber}
                        </Typography>
                        <Chip
                          label={project.pm}
                          size="small"
                          sx={{ bgcolor: project.pmColor, color: 'white' }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {project.name}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        Budgeted: {project.budgetHours} hrs
                      </Typography>
                      <Typography variant="body2">
                        Actual: {project.actualHours} hrs
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: isOverBudget ? 'error.main' : 'success.main'
                        }}
                      >
                        {variance > 0 ? '+' : ''}{variance} hrs
                      </Typography>
                      <Typography
                        variant="caption"
                        color={isOverBudget ? 'error.main' : 'success.main'}
                      >
                        ({variancePercentage.toFixed(1)}%)
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Chip
                        label={
                          Math.abs(variancePercentage) < 5 ? 'On Target' :
                          isOverBudget ? 'Over Budget' : 'Under Budget'
                        }
                        color={
                          Math.abs(variancePercentage) < 5 ? 'success' :
                          isOverBudget ? 'error' : 'info'
                        }
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// Team Utilization Report Component
const TeamUtilizationReport = () => {
  const calculateWeekHours = (installer) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    return days.reduce((sum, day) => {
      const dayJobs = mockSchedule[installer]?.[day] || [];
      return sum + dayJobs.reduce((daySum, job) => daySum + job.hours, 0);
    }, 0);
  };

  const teamData = mockInstallers.map(installer => {
    const weekHours = calculateWeekHours(installer.name);
    const utilizationRate = (weekHours / installer.maxHours) * 100;
    const status =
      utilizationRate > 100 ? 'overloaded' :
      utilizationRate >= 95 ? 'at-capacity' :
      utilizationRate >= 75 ? 'optimal' :
      'under-utilized';

    return {
      ...installer,
      weekHours,
      utilizationRate,
      status
    };
  });

  const overloadedTeam = teamData.filter(t => t.status === 'overloaded');
  const underUtilizedTeam = teamData.filter(t => t.status === 'under-utilized');
  const avgUtilization = teamData.reduce((sum, t) => sum + t.utilizationRate, 0) / teamData.length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Team Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Team Size
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {mockInstallers.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                active installers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Average Utilization
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {avgUtilization.toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of available capacity
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: overloadedTeam.length > 0 ? 'error.50' : 'success.50' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Overloaded Team
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: overloadedTeam.length > 0 ? 'error.main' : 'success.main' }}>
                {overloadedTeam.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                need workload adjustment
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Available Capacity
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {underUtilizedTeam.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                can take more work
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Individual Team Member Utilization */}
      <Typography variant="h6" gutterBottom>
        Individual Team Utilization
      </Typography>

      <Grid container spacing={2}>
        {teamData.map((member) => {
          const getStatusColor = (status) => {
            switch (status) {
              case 'overloaded': return 'error';
              case 'at-capacity': return 'warning';
              case 'optimal': return 'success';
              case 'under-utilized': return 'info';
              default: return 'default';
            }
          };

          const getStatusLabel = (status) => {
            switch (status) {
              case 'overloaded': return 'Overloaded';
              case 'at-capacity': return 'At Capacity';
              case 'optimal': return 'Optimal';
              case 'under-utilized': return 'Available';
              default: return 'Unknown';
            }
          };

          return (
            <Grid item xs={12} sm={6} md={4} key={member.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: member.color, width: 40, height: 40 }}>
                      {member.name[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {member.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Max: {member.maxHours}h/week
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Scheduled: {member.weekHours}/{member.maxHours} hours
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(member.utilizationRate, 100)}
                      color={getStatusColor(member.status)}
                      sx={{ height: 8, borderRadius: 4, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {member.utilizationRate.toFixed(0)}% utilization
                    </Typography>
                  </Box>

                  <Chip
                    label={getStatusLabel(member.status)}
                    color={getStatusColor(member.status)}
                    size="small"
                    sx={{ width: '100%' }}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Workload Balance Recommendations */}
      {(overloadedTeam.length > 0 || underUtilizedTeam.length > 0) && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Workload Balance Recommendations:
          </Typography>
          {overloadedTeam.length > 0 && (
            <Typography variant="body2">
              • Consider redistributing work from {overloadedTeam.map(t => t.name).join(', ')} to available team members
            </Typography>
          )}
          {underUtilizedTeam.length > 0 && (
            <Typography variant="body2">
              • {underUtilizedTeam.map(t => t.name).join(', ')} have capacity for additional assignments
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

// Main ProjectReports Component
const ProjectReports = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExport = () => {
    // Placeholder for export functionality
    alert('Export functionality will be implemented in Phase 2');
  };

  return (
    <Box>
      {/* Header with Export Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Project Management Reports
        </Typography>
        <Button
          startIcon={<ExportIcon />}
          variant="outlined"
          size="small"
          onClick={handleExport}
        >
          Export Reports
        </Button>
      </Box>

      {/* Report Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            icon={<TimelineIcon />}
            label="Weekly Status"
            iconPosition="start"
          />
          <Tab
            icon={<TrendIcon />}
            label="Budget Analysis"
            iconPosition="start"
          />
          <Tab
            icon={<TeamIcon />}
            label="Team Utilization"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {activeTab === 0 && <WeeklyProjectStatus />}
      {activeTab === 1 && <BudgetActualDashboard />}
      {activeTab === 2 && <TeamUtilizationReport />}
    </Box>
  );
};

export default ProjectReports;